/**
 * A session's queue of turns, in three parts:
 *   - when its prompts reach OpenCode: in the request that sends them, or later from the turn
 *     runner, which sends one at a time (`heldBehind`, `pickNextTurn`);
 *   - which queued turns can still change (`CHANGEABLE_TURN`) or a send pinned (`isPinnedTurn`),
 *     and what an edit of one saves (`editablePrompt`, `editedPrompt`);
 *   - how a move reorders them (`applyMove`, `indexPermutation`).
 *
 * A person's queued follow-up waits in cubic (status `queued`, no inbox ID) until nothing would
 * reach the agent before it, and only then does a send claim it (`claimTurnAdmission`). OpenCode
 * keeps the first text it receives under a turn's fixed prompt ID, so a follow-up that waits here
 * is the only kind whose text and place can still change. A steer passes the waiting follow-ups
 * and reaches the agent's next step, as it does in OpenCode's own inbox.
 *
 * Pure: the prompt route (`admitOrHoldNewTurn`) and the turn runner apply these rules, and the
 * edit and move of a waiting follow-up (`queued-turn-writes.ts`) read the same ones as the tray.
 */

import type { Prisma } from '@/generated/prisma/client';
import type { CodeTurnDelivery, CodeTurnStatus } from '@/generated/prisma/enums';

import { parseAssistantCitations } from './assistant-citations';
import { isWorkingTurnStatus, LIVE_TURN_STATUSES, UNDELIVERED_TURN_STATUSES } from './turn-utils';

/**
 * The earlier turns a new prompt at `index` waits behind instead of going to OpenCode in its own
 * request: a Prisma `where` the caller adds the session to. A follow-up waits behind any live
 * turn, working or waiting, so it stays in cubic until the runner reaches it. A steer waits behind
 * an earlier steer cubic has not handed to OpenCode, which keeps steers in order, and behind an
 * unconfirmed initial prompt at index 0, so startup's task goes first. Other waiting follow-ups
 * never hold it back once the session sent its first own prompt (`firstPromptSent`). Until then,
 * any earlier undelivered turn holds a steer, including a first prompt after copied history.
 */
export function heldBehind(
  delivery: CodeTurnDelivery,
  index: number,
  firstPromptSent = true,
): Prisma.CodeSessionTurnWhereInput {
  return delivery === 'queue'
    ? { index: { lt: index }, status: { in: [...LIVE_TURN_STATUSES] } }
    : {
        index: { lt: index },
        ...(firstPromptSent ? { OR: [{ delivery: 'steer' as const }, { index: 0 }] } : {}),
        status: { in: [...UNDELIVERED_TURN_STATUSES] },
        inboxId: null,
      };
}

/** A live turn as the runner chooses among them. */
export type RunnableTurn = {
  id: string;
  index: number;
  status: CodeTurnStatus;
  delivery: CodeTurnDelivery;
  admissionAttemptedAt: Date | null;
};

/**
 * The turns that show a session sent OpenCode a prompt of its own: a send claimed them
 * (`claimTurnAdmission`), and no rewind discarded them. The copies of another session's turns
 * that a fork or a side agent starts with carry no claim. A Prisma `where` the caller adds the
 * session to.
 */
export const SENT_TURN = {
  admissionAttemptedAt: { not: null },
  discardedAt: null,
} as const satisfies Prisma.CodeSessionTurnWhereInput;

/**
 * The turn a session's runner takes next among `turns`, its live turns, leaving out those this
 * runner already ran:
 *   1. an admitted or running turn, or an attempted send whose response may be lost, lowest
 *      index first: it is watched or retried before later turns;
 *   2. then, once the session sent its first prompt (`firstPromptSent`, from `SENT_TURN`), an
 *      unattempted waiting steer, lowest index first: steers reach the agent before queued work.
 *      Until then the head goes first, and a steer sent while the session started runs after the
 *      session's task instead of reaching the agent before it;
 *   3. then the waiting turn with the lowest index: the head of the queue.
 * So the runner claims an unattempted waiting follow-up only as the head of the queue, and sends
 * one ahead of a waiting steer only as the session's first prompt.
 */
export function pickNextTurn<T extends RunnableTurn>(
  turns: readonly T[],
  ran: ReadonlySet<string>,
  { firstPromptSent }: { firstPromptSent: boolean },
): T | undefined {
  const open = turns.filter((turn) => !ran.has(turn.id)).sort((a, b) => a.index - b.index);
  return (
    open.find((turn) => isWorkingTurnStatus(turn.status) || turn.admissionAttemptedAt) ??
    (firstPromptSent ? open.find((turn) => turn.delivery === 'steer') : undefined) ??
    open[0]
  );
}

/**
 * A person's follow-up that waits in cubic and that no send has claimed: OpenCode cannot hold it,
 * so its text, skills, files and place can still change. Its author may edit it, and anyone who
 * may write to the session may move it (`queued-turn-writes.ts`). Steers, check-ins and other
 * agents' messages never change.
 *
 * Plain data: the guarded writes spread it into a Prisma `where`, and the tray tests a row
 * against it (`isChangeableTurn`), so the two cannot disagree.
 */
export const CHANGEABLE_TURN = {
  index: { gt: 0 },
  status: 'queued',
  delivery: 'queue',
  inboxId: null,
  admissionAttemptedAt: null,
  discardedAt: null,
  checkInReason: null,
  messageFrom: null,
} as const satisfies Prisma.CodeSessionTurnWhereInput;

type ChangeableField = keyof typeof CHANGEABLE_TURN;

/**
 * Whether `turn` matches `CHANGEABLE_TURN`. A row that leaves a field out, as one a window on
 * older code cached, does not match.
 */
export function isChangeableTurn(turn: { readonly [K in ChangeableField]?: unknown }): boolean {
  return (Object.keys(CHANGEABLE_TURN) as ChangeableField[]).every((field) =>
    field === 'index'
      ? typeof turn.index === 'number' && turn.index > CHANGEABLE_TURN.index.gt
      : turn[field] === CHANGEABLE_TURN[field],
  );
}

/**
 * Whether a send claimed `turn` while it still reads queued, as after a send whose outcome was
 * unclear: OpenCode may hold its prompt, so it no longer changes, though Steer and Remove still
 * work. A row that leaves the claim out, as one a window on older code cached, is not pinned.
 */
export function isPinnedTurn(turn: {
  readonly status: unknown;
  readonly admissionAttemptedAt?: unknown;
}): boolean {
  return turn.status === 'queued' && turn.admissionAttemptedAt != null;
}

/** A queued prompt as its author edits it. */
export type EditablePrompt = {
  /** What the author wrote, which an edit replaces. */
  text: string;
  /**
   * The file comments and quotes the composer appended after it, which an edit keeps as they were
   * queued; '' when it had none.
   */
  context: string;
};

/**
 * Split a saved prompt into what its author wrote and the context the composer appended after it.
 * The composer saves the author's words as `authoredText` only when it appended context, and the
 * prompt then begins with them. A request cubic worded for a button saves the short text the person
 * would have typed, which the prompt does not begin with: it has no words of the author's to edit,
 * so null.
 */
export function editablePrompt(turn: {
  prompt: string;
  authoredText?: string | null;
}): EditablePrompt | null {
  if (turn.authoredText === null || turn.authoredText === undefined) {
    return { text: turn.prompt, context: '' };
  }
  return turn.prompt.startsWith(turn.authoredText)
    ? { text: turn.authoredText, context: turn.prompt.slice(turn.authoredText.length) }
    : null;
}

/** Whether `block` holds nothing but quotes, which their parser reads after a blank line. */
function isQuotesOnly(block: string): boolean {
  const { text, citations } = parseAssistantCitations(`\n\n${block}`);
  return citations.length > 0 && text === '';
}

/**
 * The `prompt` and `authoredText` an edit saves: the new words, then the context the prompt keeps,
 * joined as the composer joins them. Each of its serializers puts a blank line between words and
 * its block. With no words, the file comments' (`serializeFileCommentsPrompt`) leaves the blank
 * line out, and the quotes' (`serializeAssistantCitations`) keeps it, since their parser reads
 * quotes only after one. With no context the prompt is all the author's, so `authoredText` is
 * null, as the composer saves it.
 */
export function editedPrompt(
  text: string,
  context: string,
): { prompt: string; authoredText: string | null } {
  if (context === '') {
    return { prompt: text, authoredText: null };
  }
  const block = context.startsWith('\n\n') ? context.slice(2) : context;
  const separator = text !== '' || isQuotesOnly(block) ? '\n\n' : '';
  return { prompt: `${text}${separator}${block}`, authoredText: text };
}

/** Put `turnId` just before `beforeTurnId`, or last when that is null. */
export type QueueMove = { turnId: string; beforeTurnId: string | null };

/**
 * `order` with `move` applied, places counted once the moving turn is taken out, as T3 Code
 * counts them. Null when either turn is not in `order`, or the turn would go before itself. A move
 * to where the turn already is returns an equal order.
 */
export function applyMove(order: readonly string[], move: QueueMove): string[] | null {
  if (!order.includes(move.turnId)) {
    return null;
  }
  const rest = order.filter((id) => id !== move.turnId);
  const at = move.beforeTurnId === null ? rest.length : rest.indexOf(move.beforeTurnId);
  return at === -1 ? null : [...rest.slice(0, at), move.turnId, ...rest.slice(at)];
}

/**
 * The index each turn of `rows` takes once they run in `order`: they trade the indexes they hold,
 * the first of `order` taking the smallest. The session keeps its set of indexes, which rewind,
 * forks and MCP's "Turn N" read, and any turn outside `rows` keeps its own. Lists only the turns
 * whose index changes, by the index they hold.
 */
export function indexPermutation(
  rows: readonly { id: string; index: number }[],
  order: readonly string[],
): { id: string; from: number; to: number }[] {
  const indexes = new Map(rows.map((row) => [row.id, row.index]));
  const slots = [...indexes.values()].sort((a, b) => a - b);
  if (
    order.length !== slots.length ||
    new Set(order).size !== order.length ||
    order.some((id) => !indexes.has(id))
  ) {
    throw new Error('A queue order must list each of its turns once');
  }
  return order
    .flatMap((id, at) => {
      const from = indexes.get(id)!;
      const to = slots[at]!;
      return from === to ? [] : [{ id, from, to }];
    })
    .sort((a, b) => a.from - b.from);
}
